// Components/TelefonoInput.tsx — campo de teléfono con código de país (round 2026-10-30).
//
// Decisión del dueño: el teléfono de un usuario se guarda SIEMPRE con código de país («así formateamos bien desde el comienzo»).
// Este campo es la única puerta de los formularios: un selector de país (Chile +56 por defecto) a la izquierda y el número a la
// derecha, y hacia el formulario sale SOLO el valor canónico (+56912345678). La regla vive en `utils/telefonoE164.ts` (la misma
// que aplicará el servidor); acá solo se la usa.
//
// Cómo se usa (dentro de un `Form.Item` de Ant Design, que le da `value`, `onChange` e `id`):
//
//   <Form.Item name="telefono" label="Teléfono" rules={[reglaTelefono({ valorInicial: registro?.telefono })]}>
//     <TelefonoInput ariaLabel="Teléfono del aseador" />
//   </Form.Item>
//
// Qué hace:
//   · Escribir el número nacional (con espacios, guiones, 09…): se compone con el país elegido.
//   · Pegar un número completo con «+» o «00»: el selector toma su código y el campo queda con el número nacional.
//   · «Otro código…»: se escribe el código a mano y tiene que ser uno CONOCIDO exacto: «99», «3» o «568» (el comienzo de uno real, o
//     uno real más un dígito) se rechazan con «No reconocemos ese código de país» en vez de reinterpretarse como +992, +351 o +56.
//   · Si el número ya empieza con el código elegido y es más largo que cualquier número de ese país («56 9 8765 4321» con Chile
//     elegido) se rechaza diciendo que ya incluye el código, en vez de guardar +5656987654321.
//   · Un número copiado de WhatsApp o de Contactos llega con caracteres invisibles (marcas de dirección) y guiones largos: se
//     aceptan, igual que los espacios y guiones normales.
//   · Accesibilidad: reenvía `id`, `aria-invalid`, `aria-describedby` y `aria-required` (los que `Form.Item` inyecta) al número y al
//     selector de país, y el estado de error al selector: un lector de pantalla anuncia que el teléfono es inválido y lee el mensaje.
//   · Un dato antiguo guardado SIN código aparece con la marca «Falta el código de país». Si es inequívocamente chileno se
//     ofrece «Usar +56…», pero NUNCA se aplica solo: la persona lo confirma.
//   · Lo que no se puede componer sigue saliendo hacia el formulario (como el texto escrito, con el código delante) para que su
//     validador (`reglaTelefono`) lo rechace con un mensaje: nada se descarta en silencio ni se recorta (no hay `maxLength`).
//
// Solo Ant Design y utilidades propias: sin librerías de teléfonos ni de banderas.
import React, { useEffect, useRef, useState } from "react";
import { Button, Input, Select, Typography } from "antd";
import { formatPhoneConCodigo } from "../utils/formatPhone";
import {
  OPCION_OTRO,
  PAISES_SELECTOR,
  PAIS_POR_DEFECTO,
  PREFIJO_CODIGO_INEXISTENTE,
  componerTelefono,
  esCodigoPaisConocido,
  interpretarTelefonoGuardado,
  traeCodigoDePais,
  type ResultadoTelefono,
} from "../utils/telefonoE164";

const { Text } = Typography;

/**
 * Color de los avisos de este campo («Falta el código de país», teléfono vacío). El naranja del tema (#F59E0B) tiene un contraste
 * de 2,15:1 sobre blanco —casi ilegible, y justo es el mensaje que la persona tiene que leer para corregir un dato—; este llega a
 * 5,0:1 (la prueba lo calcula con la fórmula WCAG; el mínimo es 4,5:1). Solo cambia estos textos, no el tema global.
 */
export const COLOR_AVISO_TELEFONO = "#B45309";

export interface TelefonoInputProps {
  /** Valor del formulario: el teléfono canónico (o un dato antiguo sin código, o lo escrito aún incompleto). */
  value?: string | null;
  /** Recibe el valor CANÓNICO (+56912345678), "" si quedó vacío, o lo escrito si todavía no se puede componer. */
  onChange?: (valor: string) => void;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  /** Lo inyecta `Form.Item` para unir la etiqueta con el campo. */
  id?: string;
  /**
   * Los tres atributos de accesibilidad que `Form.Item` inyecta al hijo cuando el campo tiene un error o es obligatorio: sin
   * reenviarlos, un lector de pantalla no anuncia que el teléfono es inválido ni lee el mensaje de error (un `Input` normal sí).
   * Van al número y al selector de país, porque el error puede ser de cualquiera de los dos.
   */
  "aria-describedby"?: string;
  "aria-invalid"?: React.AriaAttributes["aria-invalid"];
  "aria-required"?: React.AriaAttributes["aria-required"];
  /** Estado de error/aviso de Ant Design. Dentro de un `Form.Item` lo da el propio formulario; el selector de país lo necesita explícito. */
  status?: "error" | "warning";
  disabled?: boolean;
  placeholder?: string;
  /** Nombre accesible del campo (lector de pantalla). Por defecto «Teléfono». */
  ariaLabel?: string;
  /**
   * `true` solo cuando es el teléfono de QUIEN USA la pantalla (Mi perfil): el navegador puede sugerirle el suyo
   * (`autoComplete="tel-national"`). En el resto —crear o editar a OTRA persona— queda apagado, para que el navegador no
   * le ponga al nuevo usuario el teléfono de quien está en el teclado.
   */
  propio?: boolean;
  /** País que viene elegido (código sin «+»). Por defecto Chile. */
  paisPorDefecto?: string;
  size?: "small" | "middle" | "large";
  /** Aviso (en amarillo) mientras el campo está vacío, p. ej. «el asistente de WhatsApp dejará de reconocerte». */
  avisoSiVacio?: string;
}

/** Lo que no se ha sincronizado todavía con el valor que llega de afuera. */
const SIN_SINCRONIZAR = Symbol("sin-sincronizar");

/** Solo dígitos, a lo más 3 (un código de país no tiene más). */
const soloCodigo = (texto: string): string => texto.replace(/[^0-9]/g, "").slice(0, 3);

/**
 * Qué se manda al formulario por lo escrito: el canónico si se puede componer; si no, lo escrito con el código del país elegido
 * delante (para que el validador del formulario —que no ve el selector— lo juzgue igual que acá y diga POR QUÉ no sirve).
 */
function valorParaElFormulario(texto: string, codigo: string): { valor: string; resultado: ResultadoTelefono } {
  const resultado = componerTelefono(texto, codigo || null);
  if (resultado.ok) return { valor: resultado.canonico, resultado };
  const crudo = texto.trim();
  // Sin código elegido: sale lo escrito tal cual y el validador dice «falta el código de país» (si se antepusiera «+», el número
  // se leería como si empezara con un código: «912345678» sería «+91 2345678»).
  if (codigo === "" || traeCodigoDePais(crudo)) return { valor: crudo, resultado };
  // Un código escrito a mano que NO existe («99», «3», «568»): «+99…» se leería como el +992 y quedaría guardado un número que la
  // persona nunca escribió. Se marca con un «+0», que ningún código conocido empieza así, y el validador responde «No
  // reconocemos ese código de país».
  if (!esCodigoPaisConocido(codigo)) return { valor: `${PREFIJO_CODIGO_INEXISTENTE}${codigo}${crudo}`, resultado };
  return { valor: `+${codigo}${crudo}`, resultado };
}

const OPCIONES_PAIS = [
  ...PAISES_SELECTOR.map((p) => ({ value: p.codigo, label: `+${p.codigo}`, nombre: p.nombre })),
  { value: OPCION_OTRO, label: "Otro…", nombre: "Otro código…" },
];

const TelefonoInput: React.FC<TelefonoInputProps> = ({
  value,
  onChange,
  onBlur,
  id,
  "aria-describedby": ariaDescribedBy,
  "aria-invalid": ariaInvalid,
  "aria-required": ariaRequired,
  status,
  disabled,
  placeholder,
  ariaLabel = "Teléfono",
  propio = false,
  paisPorDefecto = PAIS_POR_DEFECTO,
  size,
  avisoSiVacio,
}) => {
  // El campo está en error si el propio componente lo pide o si `Form.Item` le inyectó `aria-invalid`.
  const enError = status === "error" || (!!ariaInvalid && ariaInvalid !== "false");
  const [pais, setPais] = useState<string>(paisPorDefecto);
  const [otroCodigo, setOtroCodigo] = useState<string>("");
  const [texto, setTexto] = useState<string>("");
  // Último valor que el formulario tiene de este campo (el que emitimos nosotros o el que llegó de afuera): si el `value` que
  // vuelve es ese mismo, ya está reflejado en el estado local y no hay nada que re-leer (y no se pisa lo que la persona escribe).
  const reflejado = useRef<string | null | typeof SIN_SINCRONIZAR>(SIN_SINCRONIZAR);

  const codigoEfectivo = pais === OPCION_OTRO ? otroCodigo : pais;

  const elegirCodigo = (codigo: string) => {
    if (PAISES_SELECTOR.some((p) => p.codigo === codigo)) {
      setPais(codigo);
      setOtroCodigo("");
    } else {
      setPais(OPCION_OTRO);
      setOtroCodigo(codigo);
    }
  };

  // Sincroniza el estado local cuando el valor cambia por otra vía (setFieldsValue, resetFields, datos que llegan del servidor).
  useEffect(() => {
    const crudo = typeof value === "string" ? value : null;
    if (reflejado.current === crudo) return;
    reflejado.current = crudo;
    const guardado = interpretarTelefonoGuardado(crudo);
    if (guardado.estado === "completo") {
      if (PAISES_SELECTOR.some((p) => p.codigo === guardado.codigoPais)) {
        setPais(guardado.codigoPais);
        setOtroCodigo("");
      } else {
        setPais(OPCION_OTRO);
        setOtroCodigo(guardado.codigoPais);
      }
      setTexto(guardado.nacional);
      return;
    }
    // Sin código (dato antiguo), a medio escribir, o vacío/reiniciado: vuelve el país por defecto.
    setPais(paisPorDefecto);
    setOtroCodigo("");
    setTexto(guardado.estado === "sin_codigo" ? guardado.digitos : guardado.estado === "invalido" ? guardado.texto : "");
  }, [value, paisPorDefecto]);

  const emitir = (valor: string) => {
    reflejado.current = valor;
    onChange?.(valor);
  };

  const alEscribir = (e: React.ChangeEvent<HTMLInputElement>) => {
    const escrito = e.target.value;
    const { valor, resultado } = valorParaElFormulario(escrito, codigoEfectivo);
    if (resultado.ok && !resultado.vacio && traeCodigoDePais(escrito)) {
      // Pegó un número completo: el selector toma su código y el campo queda con el número nacional.
      elegirCodigo(resultado.codigoPais);
      setTexto(resultado.nacional);
    } else {
      setTexto(escrito);
    }
    emitir(valor);
  };

  const alElegirPais = (nuevo: string) => {
    setPais(nuevo);
    emitir(valorParaElFormulario(texto, nuevo === OPCION_OTRO ? otroCodigo : nuevo).valor);
  };

  const alEscribirOtroCodigo = (e: React.ChangeEvent<HTMLInputElement>) => {
    const codigo = soloCodigo(e.target.value);
    setOtroCodigo(codigo);
    emitir(valorParaElFormulario(texto, codigo).valor);
  };

  const guardado = interpretarTelefonoGuardado(value);
  const local = componerTelefono(texto, codigoEfectivo || null);
  // La vista previa solo cuando lo que se ve es EXACTAMENTE lo que va a guardarse (no para un dato antiguo sin tocar). Con el código
  // de país separado del número: la confirmación existe para que la persona revise el código.
  const vistaPrevia =
    local.ok && !local.vacio && typeof value === "string" && value === local.canonico ? formatPhoneConCodigo(local.canonico) : null;

  const aplicarSugerencia = (sugerencia: string) => {
    const r = componerTelefono(sugerencia, null);
    if (!r.ok || r.vacio) return;
    elegirCodigo(r.codigoPais);
    setTexto(r.nacional);
    emitir(r.canonico);
  };

  const selector = (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
      <Select
        aria-label="Código de país"
        aria-describedby={ariaDescribedBy}
        aria-invalid={enError ? true : undefined}
        status={enError ? "error" : undefined}
        value={pais}
        onChange={alElegirPais}
        disabled={disabled}
        size={size}
        style={{ minWidth: 84 }}
        popupMatchSelectWidth={false}
        virtual={false}
        showSearch
        optionLabelProp="label"
        options={OPCIONES_PAIS}
        optionRender={(opcion) => (
          <span>
            {opcion.data.nombre}
            {opcion.value === OPCION_OTRO ? "" : ` +${opcion.value}`}
          </span>
        )}
        filterOption={(entrada, opcion) =>
          `${opcion?.nombre ?? ""} ${opcion?.value ?? ""}`.toLowerCase().includes(entrada.trim().replace(/^\+/, "").toLowerCase())
        }
      />
      {pais === OPCION_OTRO && (
        <Input
          aria-label="Código de otro país"
          aria-describedby={ariaDescribedBy}
          aria-invalid={enError ? true : undefined}
          status={enError ? "error" : undefined}
          placeholder="Código"
          inputMode="numeric"
          autoComplete="off"
          value={otroCodigo}
          onChange={alEscribirOtroCodigo}
          disabled={disabled}
          size={size}
          style={{ width: 72 }}
          prefix="+"
        />
      )}
    </span>
  );

  return (
    <div>
      <Input
        id={id}
        type="tel"
        inputMode="tel"
        autoComplete={propio ? "tel-national" : "off"}
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy}
        aria-invalid={ariaInvalid}
        aria-required={ariaRequired}
        status={status}
        placeholder={placeholder ?? (pais === PAIS_POR_DEFECTO ? "9 1234 5678" : "Número de teléfono")}
        value={texto}
        onChange={alEscribir}
        onBlur={onBlur}
        disabled={disabled}
        size={size}
        addonBefore={selector}
      />
      {guardado.estado === "sin_codigo" && (
        <div style={{ marginTop: 4 }} role="status">
          <Text style={{ color: COLOR_AVISO_TELEFONO }}>
            Falta el código de país.{" "}
            {guardado.sugerencia ? (
              <>
                ¿Es un número de Chile?{" "}
                <Button type="link" size="small" style={{ padding: 0, height: "auto" }} onClick={() => aplicarSugerencia(guardado.sugerencia as string)}>
                  Usar {formatPhoneConCodigo(guardado.sugerencia)}
                </Button>
              </>
            ) : (
              "Elige el país y revisa el número."
            )}
          </Text>
        </div>
      )}
      {vistaPrevia && (
        <div style={{ marginTop: 4 }} aria-live="polite">
          <Text type="secondary">Se guardará como {vistaPrevia}</Text>
        </div>
      )}
      {avisoSiVacio && guardado.estado === "vacio" && (
        <div style={{ marginTop: 4 }} role="status">
          <Text style={{ color: COLOR_AVISO_TELEFONO }}>{avisoSiVacio}</Text>
        </div>
      )}
    </div>
  );
};

export default TelefonoInput;
